import { Link, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

import { ResetStep } from './enums/resetStep';
import { SignUpFormProps } from './interfaces/signUpFormProps';
import { PrivacyPolicy } from './privacyPolicy';
import { useSignUpFormStyles } from './styles/signUpFormStyles';
import { useSignUpPageStyles } from './styles/signUpPageStyles';
import { TermsOfService } from './termsOfService';

// Suppressed Practice Invite Code field but keeping references
const SHOW_PRACTICE_INVITE_CODE = false;

const SignUpForm: React.FC<SignUpFormProps> = ({
  isSignUp,
  isConfirm,
  isForcePasswordChange,
  isPasswordReset,
  isLoading = false, // HA-232: Loading state for submit button
  setIsPasswordReset,
  errorMessage,
  firstName,
  lastName,
  email,
  password,
  confirmPassword,
  phoneNumber,
  phoneNumberError,
  dateOfBirth,
  practiceName,
  emailError,
  passwordError,
  confirmPasswordError,
  practiceError,
  privacyPolicyChecked,
  termsOfServiceChecked,
  privacyPolicyClicked,
  termsOfServiceClicked,
  handleEmailChange,
  handlePasswordChange,
  handleConfirmPasswordChange,
  handlePracticeNameChange,
  handleDateOfBirthChange,
  handlePhoneNumberChange,
  handleFirstNameChange,
  handleLastNameChange,
  handleOpenModal,
  handlePasswordReset,
  handleSubmit,
  handleVerifyAndUpdatePassword,
  setPrivacyPolicyChecked,
  setTermsOfServiceChecked,
  setPrivacyPolicyClicked,
  setTermsOfServiceClicked,
  setIsSignUp,
  tabValue,
  confirmationCode,
  handleConfirmationCodeChange,
  resetStep,
}) => {
  const theme = useTheme();
  const signUpPageStyles = useSignUpPageStyles(theme);
  const signUpFormStyles = useSignUpFormStyles(theme);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const onSubmit = (e: React.FormEvent) => {
    setFormSubmitted(true);
    if (isForcePasswordChange) {
      // For forced password change after invitation, bypass confirmation code completely
      handleVerifyAndUpdatePassword(e);
    } else if (isPasswordReset) {
      if (resetStep === ResetStep.EMAIL) {
        handlePasswordReset(e);
      } else if (resetStep === ResetStep.CODE || resetStep === ResetStep.PASSWORD) {
        // Use a single step for code and password collection
        handleVerifyAndUpdatePassword(e);
      }
    } else {
      handleSubmit(e);
    }
  };
  const getSubmitButtonText = () => {
    // HA-232: Show loading text when processing
    if (isLoading) {
      if (isSignUp) {
        return 'Signing up...';
      }
      if (isPasswordReset) {
        return 'Processing...';
      }
      if (isForcePasswordChange) {
        return 'Setting password...';
      }
      if (isConfirm) {
        return 'Confirming...';
      }

      return 'Signing in...';
    }

    if (isForcePasswordChange) {
      return 'Set New Password';
    }

    if (isPasswordReset) {
      if (resetStep === ResetStep.EMAIL) {
        return 'Send Reset Code';
      } else if (resetStep === ResetStep.CODE) {
        return 'Verify Code';
      } else if (resetStep === ResetStep.PASSWORD) {
        return 'Reset Password';
      }

      return 'Request Password Reset';
    }

    if (isConfirm) {
      return 'Confirm Account';
    }

    if (isSignUp) {
      return 'Sign up';
    }

    return 'Sign in';
  };

  return (
    <Box
      component="form"
      sx={{
        ...signUpPageStyles.form,
      }}
      onSubmit={onSubmit}
      data-testid={isSignUp ? 'signup-form' : 'login-form'}
    >
      {errorMessage && (
        <Typography color="error" variant="body2" gutterBottom>
          {errorMessage}
        </Typography>
      )}
      {isSignUp && !isConfirm && !isPasswordReset && (
        <>
          <Tooltip title="Enter your legal first name">
            <TextField
              label="First Name"
              value={firstName}
              onChange={handleFirstNameChange}
              fullWidth
              margin="normal"
              sx={signUpFormStyles.textField}
              data-testid="first-name-input"
              slotProps={{
                htmlInput: {
                  'data-testid': 'first-name-input-field',
                },
              }}
            />
          </Tooltip>
          <Tooltip title="Enter your legal last name">
            <TextField
              label="Last Name"
              value={lastName}
              onChange={handleLastNameChange}
              fullWidth
              margin="normal"
              sx={signUpFormStyles.textField}
              data-testid="last-name-input"
              slotProps={{
                htmlInput: {
                  'data-testid': 'last-name-input-field',
                },
              }}
            />
          </Tooltip>
        </>
      )}{' '}
      <TextField
        autoComplete="email"
        error={!!emailError}
        helperText={emailError}
        label="Email Address"
        value={email}
        onChange={handleEmailChange}
        fullWidth
        margin="normal"
        type="email"
        sx={signUpFormStyles.textField}
        data-testid="email-input"
        slotProps={{
          htmlInput: {
            'data-testid': 'email-input-field',
          },
        }}
      />{' '}
      {!isConfirm && !isPasswordReset && !isForcePasswordChange && (
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={handlePasswordChange}
          error={!!passwordError}
          // HA-458: on sign-in the password already exists, and may be from the old rule of 8.
          helperText={
            passwordError ||
            (isSignUp
              ? 'At least 12 characters with uppercase, lowercase, numbers, and special characters'
              : undefined)
          }
          fullWidth
          margin="normal"
          sx={signUpFormStyles.textField}
          data-testid="password-input"
          slotProps={{
            htmlInput: {
              'data-testid': 'password-input-field',
            },
          }}
        />
      )}
      {isSignUp && !isConfirm && !isPasswordReset && (
        <>
          <Tooltip title="Re-enter your password to confirm">
            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              fullWidth
              margin="normal"
              sx={signUpFormStyles.textField}
              data-testid="confirm-password-input"
              slotProps={{
                htmlInput: {
                  'data-testid': 'confirm-password-input-field',
                },
              }}
            />
          </Tooltip>
          <Tooltip title="Enter your phone number in the format: +1 (123) 456-7890">
            <TextField
              autoComplete="tel"
              error={!!phoneNumberError}
              helperText={phoneNumberError}
              label="Phone Number"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              fullWidth
              margin="normal"
              type="tel"
              sx={signUpFormStyles.textField}
              data-testid="phone-number-input"
              slotProps={{
                htmlInput: {
                  'data-testid': 'phone-number-input-field',
                },
              }}
            />
          </Tooltip>
          <Tooltip title="Enter your date of birth (MM/DD/YYYY)">
            <TextField
              autoComplete="bday"
              label="Date of Birth"
              type="date"
              value={dateOfBirth}
              onChange={handleDateOfBirthChange}
              fullWidth
              margin="normal"
              sx={signUpFormStyles.textField}
              data-testid="date-of-birth-input"
              slotProps={{
                htmlInput: {
                  'data-testid': 'date-of-birth-input-field',
                },

                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Tooltip>
          {tabValue === 'admin' && (
            <Tooltip title="Enter the name of your practice">
              <TextField
                label="Practice Name"
                value={practiceName}
                onChange={handlePracticeNameChange}
                error={!!practiceError}
                helperText={practiceError}
                fullWidth
                margin="normal"
                sx={signUpFormStyles.textField}
                data-testid="practitioner-practice-name-input"
              />
            </Tooltip>
          )}
          {tabValue === 'practiceAdmin' && (
            <Tooltip title="This is the name of the practice you are associated with. It cannot be used by other practices">
              <TextField
                label="Practice Name"
                value={practiceName}
                onChange={handlePracticeNameChange}
                error={!!practiceError}
                helperText={practiceError}
                fullWidth
                margin="normal"
                sx={signUpFormStyles.textField}
                data-testid="admin-practice-name-input"
              />
            </Tooltip>
          )}
          {SHOW_PRACTICE_INVITE_CODE && tabValue === 'practitioner' && (
            <Tooltip title="Enter the invite code provided by your practice administrator">
              <TextField
                label="Practice Invite Code"
                value={practiceName}
                onChange={handlePracticeNameChange}
                error={!!practiceError}
                helperText={practiceError}
                fullWidth
                margin="normal"
                sx={signUpFormStyles.textField}
                data-testid="practice-invite-code-input"
              />
            </Tooltip>
          )}{' '}
          <FormControlLabel
            control={
              <Checkbox
                checked={privacyPolicyChecked}
                onChange={(e) => setPrivacyPolicyChecked(e.target.checked)}
                data-testid="privacy-policy-checkbox"
              />
            }
            label={
              <Typography>
                I agree to the{' '}
                <Link
                  href="#privacy-policy"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setPrivacyPolicyClicked(true);
                    handleOpenModal(PrivacyPolicy, 'Privacy Policy');
                  }}
                >
                  Privacy Policy
                </Link>
              </Typography>
            }
            sx={{
              color: formSubmitted && !privacyPolicyClicked ? 'red' : 'inherit',
              marginLeft: theme.spacing(0.5),
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={termsOfServiceChecked}
                onChange={(e) => setTermsOfServiceChecked(e.target.checked)}
              />
            }
            label={
              <Typography>
                I agree to the{' '}
                <Link
                  href="#terms-of-service"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setTermsOfServiceClicked(true);
                    handleOpenModal(TermsOfService, 'Terms of Service');
                  }}
                >
                  Terms of Service
                </Link>
              </Typography>
            }
            sx={{
              ...signUpFormStyles,
              color: formSubmitted && !termsOfServiceClicked ? 'red' : 'inherit',
            }}
          />
        </>
      )}
      {isConfirm && (
        <TextField
          label="Confirmation Code"
          value={confirmationCode}
          onChange={handleConfirmationCodeChange}
          fullWidth
          margin="dense"
          sx={signUpFormStyles.textField}
          data-testid="account-confirmation-code-input"
        />
      )}{' '}
      {isPasswordReset && resetStep === ResetStep.CODE && !isForcePasswordChange && (
        <>
          <TextField
            label="Confirmation Code"
            value={confirmationCode}
            onChange={handleConfirmationCodeChange}
            fullWidth
            margin="normal"
            sx={signUpFormStyles.textField}
            autoFocus
            data-testid="password-reset-confirmation-code-input"
            slotProps={{
              htmlInput: {
                'data-testid': 'password-reset-confirmation-code-input-field',
              },
            }}
          />
          {/* Show password fields immediately after entering code */}
          {confirmationCode && confirmationCode.trim() !== '' && (
            <>
              <TextField
                label="New Password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                error={!!passwordError}
                helperText={passwordError}
                fullWidth
                margin="normal"
                sx={signUpFormStyles.textField}
                data-testid="new-password-code-step-input"
              />
              <Tooltip title="Re-enter your new password to confirm">
                <TextField
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  error={!!confirmPasswordError}
                  helperText={confirmPasswordError}
                  fullWidth
                  margin="normal"
                  sx={signUpFormStyles.textField}
                  data-testid="confirm-new-password-code-step-input"
                />
              </Tooltip>
            </>
          )}
        </>
      )}
      {isPasswordReset && resetStep === ResetStep.PASSWORD && !isForcePasswordChange && (
        <>
          <TextField
            label="New Password"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            error={!!passwordError}
            helperText={passwordError}
            fullWidth
            margin="normal"
            sx={signUpFormStyles.textField}
            autoFocus
          />
          <Tooltip title="Re-enter your new password to confirm">
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              fullWidth
              margin="normal"
              sx={signUpFormStyles.textField}
            />
          </Tooltip>
        </>
      )}{' '}
      {isForcePasswordChange && (
        <>
          <Typography variant="body1" color="primary" sx={{ mt: 1, mb: 2, fontWeight: 'medium' }}>
            Please set a new password for your account
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
            Your temporary password has been accepted. You need to create a permanent password.
          </Typography>
          <TextField
            label="New Password"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            error={!!passwordError}
            helperText={passwordError || "Create a strong password that you'll remember"}
            fullWidth
            margin="normal"
            sx={signUpFormStyles.textField}
            autoFocus
          />
          <Tooltip title="Re-enter your new password to confirm">
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              fullWidth
              margin="normal"
              sx={signUpFormStyles.textField}
            />
          </Tooltip>
        </>
      )}{' '}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        sx={signUpPageStyles.button}
        data-testid={isSignUp ? 'signup-submit-button' : 'login-submit-button'}
      >
        {getSubmitButtonText()}
      </Button>{' '}
      {!isConfirm && !isPasswordReset && !isForcePasswordChange && (
        <Button
          onClick={() => setIsSignUp(!isSignUp)}
          fullWidth
          variant="text"
          sx={{ mt: 2 }}
          data-testid={isSignUp ? 'switch-to-login-button' : 'switch-to-signup-button'}
        >
          {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
        </Button>
      )}{' '}
      {!isConfirm && !isSignUp && !isPasswordReset && !isForcePasswordChange && (
        <Button
          onClick={() => setIsPasswordReset(true)}
          fullWidth
          variant="text"
          sx={{ mt: 2 }}
          data-testid="forgot-password-button"
        >
          Forgot Password?
        </Button>
      )}{' '}
      {isPasswordReset && (
        <Button
          onClick={() => {
            // Reset all password reset related state
            setIsPasswordReset(false);

            // Clear all relevant fields including the email
            handleEmailChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
            handlePasswordChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
            handleConfirmPasswordChange({
              target: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>);
            handleConfirmationCodeChange({
              target: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>);

            // We DON'T want to call handlePasswordReset here because that would
            // start the password reset flow again, which is not what we want when canceling
          }}
          fullWidth
          variant="text"
          sx={{ mt: 2 }}
          data-testid="cancel-password-reset-button"
        >
          Cancel Password Reset
        </Button>
      )}
    </Box>
  );
};

export default SignUpForm;
